import type { Job } from '@maker-toolchest/shared';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { StatusBadge } from '@/components/StatusBadge';
import { JobActionsMenu } from './JobActionsMenu';

interface JobCardProps {
  job: Job;
  onAddTasks: (job: Job) => void;
  onUpdateStatus: (job: Job) => void;
  onViewTasks: (job: Job) => void;
  onCancelJob: (job: Job) => void;
}

export function JobCard({
  job,
  onAddTasks,
  onUpdateStatus,
  onViewTasks,
  onCancelJob,
}: JobCardProps) {
  const navigate = useNavigate();

  return (
    <div className="relative rounded border border-border bg-background p-4 transition-colors hover:bg-background-subtle">
      <div
        onClick={() => navigate(`/projects/jobs/${job.id}`)}
        className="cursor-pointer"
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1">
            {/* JobTypeIcon hidden for now - only 3D printing supported */}
            <div className="flex-1">
              <h3 className="font-medium text-foreground">{job.name}</h3>
              {job.description && (
                <p className="mt-1 text-sm text-foreground-muted line-clamp-2">{job.description}</p>
              )}
              <div className="mt-2 flex items-center gap-4 text-xs text-foreground-muted">
                {/* Job type badge hidden for now - only 3D printing supported */}
                <StatusBadge status={job.status} size="sm" />
                <span>
                  {job.quantity} task{job.quantity > 1 ? 's' : ''}
                </span>
                <span>
                  Created {formatDistanceToNow(new Date(job.createdAt), { addSuffix: true })}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-4">
            <div className="text-right">
              <div className="text-sm font-medium text-foreground">
                {Math.round(job.progress)}%
              </div>
              <div className="mt-1 h-1.5 w-16 rounded-full bg-background-subtle">
                <div
                  className="h-full rounded-full bg-primary transition-all"
                  style={{ width: `${job.progress}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Menu - positioned in top-right */}
      <div className="absolute top-4 right-4">
        <JobActionsMenu
          job={job}
          onAddTasks={() => onAddTasks(job)}
          onUpdateStatus={() => onUpdateStatus(job)}
          onViewTasks={() => onViewTasks(job)}
          onCancelJob={() => onCancelJob(job)}
        />
      </div>
    </div>
  );
}
